import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import Register from "./pages/Register";
import UserLogin from "./pages/UserLogin";
import UserRegister from "./pages/UserRegister";
import Dashboard from "./pages/Dashboard";
import DeactivatedProfiles from "./pages/DeactivatedProfiles";
import PublicProfiles from "./pages/PublicProfiles";
import ClientManagement from "./pages/ClientManagement";
import ReferralManagement from "./pages/ReferralManagement";
import ClientReferrals from "./pages/ClientReferrals";
import { FullScreenLoader } from "./components/Skeleton";
import ClientDashboard from "./pages/ClientDashboard";
import FindMatches from "./pages/FindMatches";
import MyInterests from "./pages/MyInterests";
import BureauInterests from "./pages/BureauInterests";
import BureauMembers from "./pages/BureauMembers";
import Notifications from "./pages/Notifications";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Bureaus from "./pages/Bureaus";
import MyBureau from "./pages/MyBureau";
import BureauAssociations from "./pages/BureauAssociations";
import NotFound from "./pages/NotFound";

import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { LoadingProvider } from "./contexts/LoadingContext";
import { apiFetch } from "./api";
import GlobalLoadingSpinner from "./components/GlobalLoadingSpinner";
import Sidebar from "./components/Sidebar";
import { useLocation, useNavigate } from "react-router-dom";

// Auth-form pages where, if a session is already active in this browser,
// we should auto-redirect to the corresponding home page instead of
// asking the user to sign in again. Public-profiles is intentionally
// excluded so unauthenticated browsing still works.
const AUTH_PATHS = ["/", "/login", "/register", "/bureau", "/bureau/register"];

// Hook: on every auth-page navigation, check for an active session and
// redirect away. Both checks are synchronous via localStorage flags so a
// brand-new tab redirects immediately without waiting on a network round
// trip — and so cross-origin cookie quirks (HttpOnly bureau cookie not
// sent on the first paint) don't keep the user on the login form. The
// flags are set on login (Login.jsx, UserLogin.jsx) and cleared on logout.
function useAutoRedirectFromAuth() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!AUTH_PATHS.includes(location.pathname)) return;
    let cancelled = false;

    const publicToken = localStorage.getItem("publicUserToken");
    if (publicToken) {
      navigate("/find-matches", { replace: true });
      return;
    }

    if (localStorage.getItem("bureauActive") === "true") {
      navigate("/bureau/dashboard", { replace: true });
      return;
    }

    // Fallback: if the flag is missing for some reason but a bureau cookie
    // is still active (e.g. a user logged in before this flag existed), the
    // /auth/profile call will succeed and we redirect anyway, then cache
    // the flag for next time.
    apiFetch("/auth/profile")
      .then((bureau) => {
        if (cancelled || !bureau) return;
        try { localStorage.setItem("bureauActive", "true"); } catch { /* */ }
        navigate("/bureau/dashboard", { replace: true });
      })
      .catch(() => {
        // No bureau session — leave the user on the auth page.
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);
}

// Bureau Admin Authentication
const BureauPrivateRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await apiFetch("/auth/profile");
        // Cookie is valid — make sure the visibility flag agrees.
        try { localStorage.setItem("bureauActive", "true"); } catch { /* */ }
        setIsAuthenticated(true);
      } catch (err) {
        console.error("Auth check failed:", err);
        // Cookie is invalid/expired — clear the stale flag so the
        // auto-redirect hook doesn't keep ping-ponging us back here.
        try { localStorage.removeItem("bureauActive"); } catch { /* */ }
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  if (isAuthenticated === null) return <FullScreenLoader />;

  return isAuthenticated ? children : <Navigate to="/bureau" />;
};

// Public User Authentication
const PublicPrivateRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("publicUserToken");
        if (!token) {
          setIsAuthenticated(false);
          return;
        }
        // Verify token is still valid
        await apiFetch("/public/auth/profile");
        setIsAuthenticated(true);
      } catch (err) {
        console.error("Public auth check failed:", err);
        localStorage.removeItem("publicUserToken");
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  if (isAuthenticated === null) return <FullScreenLoader />;

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Routes that get the persistent sidebar (md+) and corresponding content
// shift. Auth pages (/login, /register, /bureau, /public-profiles, etc.)
// are deliberately excluded so they render full-bleed.
const APP_PATHS = [
  "/bureau/dashboard",
  "/bureau/clients",
  "/bureau/referrals",
  "/bureau/deactivated",
  "/bureau/interest-requests",
  "/bureau/members",
  "/bureau/association-requests",
  "/notifications",
  "/bureaus",
  "/my-bureau",
  "/my-referrals",
  "/my-interests",
  "/find-matches",
  "/saved-profiles",
];

function AppShell() {
  const location = useLocation();
  useAutoRedirectFromAuth();

  const showSidebar = APP_PATHS.some(
    (p) =>
      location.pathname === p || location.pathname.startsWith(p + "/")
  );

  return (
    <>
      {showSidebar && <Sidebar />}
      {/* Sidebar is always physically collapsed (w-16) — it visually expands
          on hover as an overlay above the page content, so the content shift
          can stay constant and never reflows. */}
      <div className={showSidebar ? "md:pl-16" : ""}>
        <Routes>
          {/* ===== PUBLIC USER ROUTES ===== */}
          {/* Public User Login */}
          <Route path="/" element={<UserLogin />} />
          <Route path="/login" element={<UserLogin />} />
          <Route path="/register" element={<UserRegister />} />

          {/* Public Profiles - No Authentication Required */}
          <Route path="/public-profiles" element={<PublicProfiles />} />

          {/* Public User Protected Routes */}
          <Route
            path="/saved-profiles"
            element={
              <PublicPrivateRoute>
                <Dashboard /> {/* Reuse Dashboard for saved profiles */}
              </PublicPrivateRoute>
            }
          />
          <Route
            path="/my-referrals"
            element={
              <PublicPrivateRoute>
                <ClientDashboard />
              </PublicPrivateRoute>
            }
          />
          <Route
            path="/find-matches"
            element={
              <PublicPrivateRoute>
                <FindMatches />
              </PublicPrivateRoute>
            }
          />
          <Route
            path="/my-interests"
            element={
              <PublicPrivateRoute>
                <MyInterests />
              </PublicPrivateRoute>
            }
          />
          {/* Bureau directory — anonymous-browsable. Requesting association
              inside the page redirects to login if not signed in. */}
          <Route path="/bureaus" element={<Bureaus />} />
          <Route
            path="/my-bureau"
            element={
              <PublicPrivateRoute>
                <MyBureau />
              </PublicPrivateRoute>
            }
          />

          {/* ===== BUREAU ADMIN ROUTES ===== */}
          {/* Bureau Admin Login */}
          <Route path="/bureau" element={<Login />} />
          <Route path="/bureau/register" element={<Register />} />

          {/* Forgot/reset password — public, no auth. Used by bureau members. */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Bureau Admin Protected Routes */}
          <Route
            path="/bureau/dashboard"
            element={
              <BureauPrivateRoute>
                <Dashboard />
              </BureauPrivateRoute>
            }
          />
          <Route
            path="/bureau/clients"
            element={
              <BureauPrivateRoute>
                <ClientManagement />
              </BureauPrivateRoute>
            }
          />
          <Route
            path="/bureau/referrals"
            element={
              <BureauPrivateRoute>
                <ReferralManagement />
              </BureauPrivateRoute>
            }
          />
          <Route
            path="/bureau/referrals/:clientId"
            element={
              <BureauPrivateRoute>
                <ClientReferrals />
              </BureauPrivateRoute>
            }
          />
          <Route
            path="/bureau/deactivated"
            element={
              <BureauPrivateRoute>
                <DeactivatedProfiles />
              </BureauPrivateRoute>
            }
          />
          <Route
            path="/bureau/interest-requests"
            element={
              <BureauPrivateRoute>
                <BureauInterests />
              </BureauPrivateRoute>
            }
          />
          <Route
            path="/bureau/association-requests"
            element={
              <BureauPrivateRoute>
                <BureauAssociations />
              </BureauPrivateRoute>
            }
          />
          <Route
            path="/bureau/members"
            element={
              <BureauPrivateRoute>
                <BureauMembers />
              </BureauPrivateRoute>
            }
          />
          {/* Shared notifications inbox for both roles. The component picks
              the right API base from the active token at request time. */}
          <Route path="/notifications" element={<Notifications />} />

          {/* ===== 404 NOT FOUND ===== */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </>
  );
}

function App() {
  return (
    <LoadingProvider>
      <LanguageProvider>
        <ThemeProvider>
          <BrowserRouter dir="auto">
            <AppShell />
          </BrowserRouter>
        </ThemeProvider>
      </LanguageProvider>
      <GlobalLoadingSpinner />
    </LoadingProvider>
  );
}

export default App;
